import { SortDirection } from '../../../core/enums/sort-direction';
import { AccessLogDateRange, AccessLogFilters, AccessLogTypeFilter } from '../models/access-log-filters';
import { AccessLogSearchRequest } from '../models/access-log-search-request';
import { AuditEventType } from '../models/audit-event-type';

/**
 * Shortest term the server accepts (`PatientConstraints.SearchRequestMinimumStringLength`). A shorter
 * term is a 400, not a wider search, so the client withholds it and leaves the list unfiltered until
 * the patient has typed enough.
 */
export const SEARCH_TERM_MINIMUM_LENGTH = 3;

/** Longest term the server accepts (`AccessLogConstraints.SearchTermMaximumLength`). */
export const SEARCH_TERM_MAXIMUM_LENGTH = 100;

/** The only sort column the server whitelists for this search. */
const SORT_COLUMN = 'CreatedOnUtc';

/** Trailing window length, in calendar days, for the fixed-length ranges. */
const DAYS_BY_RANGE: Partial<Record<AccessLogDateRange, number>> = {
  d7: 7,
  d30: 30,
  d90: 90,
};

export interface AccessLogDateWindow {
  createdAfterUtc?: string;
  createdBeforeUtc?: string;
}

function startOfLocalDay(date: Date): Date {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start;
}

function addDays(date: Date, days: number): Date {
  const shifted = new Date(date);
  shifted.setDate(shifted.getDate() + days);
  return shifted;
}

/**
 * The same calendar day one year earlier, clamped to the last valid day of the target month.
 *
 * Neither `setFullYear(y - 1)` nor `setMonth(m - 12)` is safe on 29 February: both overflow to 1 March
 * of the non-leap year, which moves the lower bound *forward* and quietly makes "Last 12 Months" a day
 * short. Clamping to 28 February keeps the window whole. Erring long is the right direction here — a
 * range that omits a day of a patient's disclosure history is the failure that matters.
 */
function subtractOneYear(date: Date): Date {
  const year = date.getFullYear() - 1;
  const month = date.getMonth();
  // Day 0 of the following month is the last day of this one.
  const lastDayOfTargetMonth = new Date(year, month + 1, 0).getDate();

  const shifted = new Date(date);
  shifted.setFullYear(year, month, Math.min(date.getDate(), lastDayOfTargetMonth));
  return shifted;
}

/**
 * Resolves a range option into the inclusive UTC window the API expects.
 *
 * Boundaries are aligned to the *patient's local* calendar day and then serialized as UTC instants:
 * "Today" has to mean their today, and the list is grouped by local day, so a window that started
 * mid-day would render a partial first group.
 *
 * Every range except "Yesterday" runs to the present, and those send **no upper bound at all**. An
 * access event cannot be recorded in the future, so a bound of "now" excludes nothing — but computing
 * it from the *browser's* clock means a client running even slightly slow silently drops the most
 * recently recorded events, which on this page are exactly the ones a patient checking who just
 * touched their record came to see. Omitting it removes that failure mode; the server only constrains
 * the lower bound (see `PatientPortalAccessLogSearchRequestValidator`), which is always in the past
 * here because it is the start of the day containing `now`.
 */
export function resolveDateWindow(range: AccessLogDateRange, now: Date): AccessLogDateWindow {
  const todayStart = startOfLocalDay(now);

  if (range === 'yesterday') {
    const yesterdayStart = addDays(todayStart, -1);
    // The one range that genuinely needs an upper bound, and the only one whose bound is in the past.
    // One millisecond before today's midnight: the server compares inclusively, so today's
    // 00:00:00.000 event would otherwise fall into "Yesterday" as well.
    return {
      createdAfterUtc: yesterdayStart.toISOString(),
      createdBeforeUtc: new Date(todayStart.getTime() - 1).toISOString(),
    };
  }

  if (range === 'today') {
    return { createdAfterUtc: todayStart.toISOString() };
  }

  if (range === 'm12') {
    return { createdAfterUtc: subtractOneYear(todayStart).toISOString() };
  }

  // "Last N Days" counts N calendar days *including today*, so the window starts N-1 days back.
  const days = DAYS_BY_RANGE[range] ?? 30;
  return { createdAfterUtc: addDays(todayStart, -(days - 1)).toISOString() };
}

/**
 * Maps the coarse Type option onto server event-type ids. `undefined` means "All" — the filter is
 * omitted rather than sent as every id, so the server skips the predicate entirely.
 *
 * "Exported" is two ids: the server collapses Download and Transmit into one `Export` category, and
 * the filter has to accept the pair to match.
 */
export function toEventTypeIds(type: AccessLogTypeFilter): number[] | undefined {
  switch (type) {
    case 'view':
      return [AuditEventType.View];
    case 'print':
      return [AuditEventType.Print];
    case 'export':
      return [AuditEventType.Download, AuditEventType.Transmit];
    default:
      return undefined;
  }
}

/**
 * Normalizes a typed term into what the request may carry: trimmed, capped at the server's maximum,
 * and withheld entirely below the server's minimum. Returns `undefined` when there is nothing to send.
 */
export function toSearchTerm(searchTerm: string | null | undefined): string | undefined {
  const trimmed = (searchTerm ?? '').trim();

  if (trimmed.length < SEARCH_TERM_MINIMUM_LENGTH) {
    return undefined;
  }

  return trimmed.slice(0, SEARCH_TERM_MAXIMUM_LENGTH);
}

/**
 * Assembles the search body. Every filter travels to the server (PBI 56315 AC3); optional fields are
 * omitted rather than sent as null so the server's `HasValue` checks skip them.
 */
export function buildSearchRequest(
  filters: AccessLogFilters,
  pageNumber: number,
  pageSize: number,
  now: Date
): AccessLogSearchRequest {
  const request: AccessLogSearchRequest = {
    pageNumber,
    pageSize,
    sortColumn: SORT_COLUMN,
    sortDirection: SortDirection.Descending,
    ...resolveDateWindow(filters.dateRange, now),
  };

  const eventTypeIds = toEventTypeIds(filters.type);
  if (eventTypeIds) {
    request.eventTypeIds = eventTypeIds;
  }

  if (filters.actorId !== null) {
    request.actingPortalUserId = filters.actorId;
  }

  const searchTerm = toSearchTerm(filters.searchTerm);
  if (searchTerm) {
    request.searchTerm = searchTerm;
  }

  return request;
}

/** The Type and "Accessed by" filters, which have a true "not filtering" value. Shared by both checks below. */
function hasActiveNonDateFilters(filters: AccessLogFilters): boolean {
  return filters.type !== 'all' || filters.actorId !== null;
}

/**
 * True when any of the three dropdown filters is off its default. Drives the Clear control, which is
 * deliberately scoped to those filters — the search field has its own clear affordance, matching the
 * rest of the portal's clinical lists.
 */
export function hasActiveStructuredFilters(filters: AccessLogFilters, defaultDateRange: AccessLogDateRange): boolean {
  return filters.dateRange !== defaultDateRange || hasActiveNonDateFilters(filters);
}

/**
 * True when the patient has narrowed the view at all, search included. Chooses between the two empty
 * states: "No matching activity" (something is filtering) versus "No activity on file" (nothing is).
 *
 * Reads the *effective* search term, not the raw text: a term below the server's minimum is withheld
 * from the request, so it narrows nothing. Counting it here would tell a patient with no history at
 * all to "try changing your search or filters" — wrong, and on a compliance page the difference
 * between "we found nothing matching" and "nothing was ever recorded" is the whole message.
 *
 * The date range is compared against the **widest** option, not the default, because that is the only
 * one that sends no `createdAfterUtc` bound the patient could be hitting. The default is "Last 30
 * Days", which `resolveDateWindow` genuinely applies — so measuring against it would let a patient
 * whose last disclosure was 45 days ago be told "No activity on file", asserting that nothing was
 * ever recorded when the page only knows nothing was recorded this month, and with no widen action
 * offered to find out otherwise. `hasActiveStructuredFilters` deliberately keeps the default as its
 * baseline: the Clear control is about resetting the controls, which is a different question.
 */
export function hasActiveFilters(filters: AccessLogFilters, widestDateRange: AccessLogDateRange): boolean {
  return (
    filters.dateRange !== widestDateRange ||
    hasActiveNonDateFilters(filters) ||
    toSearchTerm(filters.searchTerm) !== undefined
  );
}
